import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { TrendingUp, Gift } from 'lucide-react';
import { HostPrimaryButton } from '../components/HostSettingsCard';
import { HostOnboardingShell } from '../components/host-onboarding/HostOnboardingShell';
import { useBackTarget } from '../hooks/useBackTarget';

const infoCards = [
  {
    id: 'earn-more',
    title: 'Earn More',
    Icon: TrendingUp,
    body: 'Earn a commission on what your guests spend at participating venues. The more guests you invite, the more you earn — plus monthly rewards as you grow.',
  },
  {
    id: 'guest-tips',
    title: 'Guest Tips',
    Icon: Gift,
    body: 'Guests can also leave you a tip after their stay, increasing your total earnings.',
  },
];

export default function HostIntro2Page() {
  const navigate = useNavigate();
  const goBack = useBackTarget();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  const isAuthed = Boolean(token && user && user.id);
  const primaryButtonLabel = user?.role === 'host' || user?.host_status === 'approved' ? 'Continue' : 'Partner Up';

  // Fresh signups arrive here from /select-role → /host-intro-1 without
  // an account yet. Send them through /register first; VerifyEmailPage
  // consumes the 'host' partner intent and routes them back to
  // /host-verify-listing after their email is confirmed. Existing hosts
  // (already authenticated) skip register and go straight to the listing
  // step, preserving the /switch-role behaviour for multi-role users.
  const handleNext = () => {
    if (isAuthed) {
      navigate('/host-verify-listing');
      return;
    }
    navigate('/register');
  };

  return (
    <HostOnboardingShell
      onBack={goBack}
      pageTestId="host-intro-2-page"
      backTestId="host-intro-2-back-button"
      logoTestId="host-intro-2-logo"
      footer={
        <HostPrimaryButton onClick={handleNext} dataTestId="host-intro-2-next-button">
          {primaryButtonLabel}
        </HostPrimaryButton>
      }
    >
      <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 16 }}>
        {infoCards.map((card, index) => (
          <motion.div
            key={card.id}
            className="premium-card"
            data-testid={`host-intro-2-${card.id}-card`}
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.48, delay: 0.15 + index * 0.12, ease: [0.25, 0.1, 0.25, 1] }}
            style={{ width: '100%', padding: '18px 18px 20px' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, marginBottom: 12 }}>
              <div
                data-testid={`host-intro-2-${card.id}-icon`}
                style={{
                  width: 46,
                  height: 46,
                  borderRadius: '50%',
                  background: 'rgba(196, 18, 48, 0.12)',
                  border: '1px solid rgba(196, 18, 48, 0.20)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <card.Icon size={22} color="var(--accent-purple)" strokeWidth={1.6} />
              </div>
              <p data-testid={`host-intro-2-${card.id}-title`} style={{ fontFamily: "'Inria Serif', serif", fontSize: 21, fontWeight: 700, color: 'var(--accent-purple)', margin: 0, lineHeight: 1.05 }}>
                {card.title}
              </p>
            </div>

            <p data-testid={`host-intro-2-${card.id}-body`} style={{ fontFamily: "'Inria Serif', serif", fontSize: 19, fontWeight: 700, color: 'var(--text)', margin: 0, lineHeight: 1.06, textAlign: 'center' }}>
              {card.body}
            </p>
          </motion.div>
        ))}
      </div>
    </HostOnboardingShell>
  );
}
